import type { AdFormValues } from 'organisms/AdForm/AdForm';

const CAMPAIGN_DRAFT_STORAGE_KEY = 'elevator:campaign-draft';

/** Bumped on clear so in-flight async saves cannot repopulate sessionStorage. */
let draftSaveGeneration = 0;

/** Test-only reset for generation counter. */
export function resetCampaignDraftSaveGeneration(): void {
  draftSaveGeneration = 0;
}

type StorableFile = {
  name: string;
  type: string;
  lastModified: number;
  dataBase64: string;
};

type StoredCampaignDraft = {
  activeStep: number;
  totalCost: number;
  campaign: Omit<AdFormValues, 'files'> & { files: StorableFile[] };
};

export type CampaignDraft = {
  activeStep: number;
  totalCost: number;
  campaign: AdFormValues;
};

const isBrowser = () => typeof window !== 'undefined';

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const storableToFile = (storable: StorableFile): File => {
  const bytes = base64ToUint8Array(storable.dataBase64);
  return new File([bytes as BlobPart], storable.name, {
    type: storable.type,
    lastModified: storable.lastModified,
  });
};

const filesToStorable = async (files: File[]): Promise<StorableFile[]> => {
  return Promise.all(
    files.map(async (file) => ({
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
      dataBase64: arrayBufferToBase64(await file.arrayBuffer()),
    })),
  );
};

/** Persists campaign flow state so auth redirects can resume checkout. */
export async function saveCampaignDraft(draft: CampaignDraft): Promise<void> {
  if (!isBrowser()) return;

  const generation = draftSaveGeneration;
  const { files, ...campaignFields } = draft.campaign;
  const storableFiles = await filesToStorable(files);

  if (generation !== draftSaveGeneration) return;

  const stored: StoredCampaignDraft = {
    activeStep: draft.activeStep,
    totalCost: draft.totalCost,
    campaign: {
      ...campaignFields,
      files: storableFiles,
    },
  };

  sessionStorage.setItem(CAMPAIGN_DRAFT_STORAGE_KEY, JSON.stringify(stored));
}

/** Restores campaign flow state after returning from auth. */
export async function loadCampaignDraft(): Promise<CampaignDraft | null> {
  if (!isBrowser()) return null;

  const raw = sessionStorage.getItem(CAMPAIGN_DRAFT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const stored = JSON.parse(raw) as StoredCampaignDraft;
    return {
      activeStep: stored.activeStep,
      totalCost: stored.totalCost,
      campaign: {
        ...stored.campaign,
        files: stored.campaign.files.map(storableToFile),
      },
    };
  } catch {
    sessionStorage.removeItem(CAMPAIGN_DRAFT_STORAGE_KEY);
    return null;
  }
}

export function clearCampaignDraft(): void {
  if (!isBrowser()) return;
  draftSaveGeneration += 1;
  sessionStorage.removeItem(CAMPAIGN_DRAFT_STORAGE_KEY);
}
